/**
 * PortalQuestionnaires - Dynamic form renderer for client questionnaires
 * Features: section grouping, auto-save, all question types, conditional questions
 * Deployed to: frontend/src/pages/portal/PortalQuestionnaires.js
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, CheckCircle, AlertCircle, Clock, Save, Send
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalQuestionnaires = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    if (assignmentId) {
      loadQuestionnaire(assignmentId);
    } else {
      loadList();
    }
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [assignmentId]);

  const loadList = async () => {
    setLoading(true);
    try {
      const data = await clientPortalService.getQuestionnaires();
      setQuestionnaires(data.questionnaires || []);
    } catch (err) {
      console.error('Failed to load questionnaires:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionnaire = async (id) => {
    setLoading(true);
    try {
      const data = await clientPortalService.getQuestionnaire(id);
      setQuestionnaire(data);
      // Populate existing responses
      const existing = {};
      (data.questions || []).forEach(q => {
        if (q.response) {
          existing[q.id] = q.response.response_text || q.response.response_data || '';
        }
      });
      setResponses(existing);
    } catch (err) {
      console.error('Failed to load questionnaire:', err);
      setError('Questionnaire not found');
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    // Debounced auto-save
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => autoSave({ ...responses, [questionId]: value }), 2000);
  };

  const autoSave = async (currentResponses) => {
    if (!questionnaire || questionnaire.status === 'completed') return;
    setSaving(true);
    try {
      const respArray = Object.entries(currentResponses).map(([qid, val]) => ({
        question_id: parseInt(qid),
        response_text: typeof val === 'string' ? val : null,
        response_data: typeof val === 'object' ? val : null,
      }));
      await clientPortalService.saveQuestionnaireResponses(questionnaire.assignment_id, respArray);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true); setError('');
    try {
      // Save first
      await autoSave(responses);
      const result = await clientPortalService.submitQuestionnaire(questionnaire.assignment_id);
      if (result.success) {
        loadQuestionnaire(questionnaire.assignment_id);
      } else {
        setError(result.error || 'Please complete all required questions');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Check conditional visibility
  const isVisible = (question) => {
    if (!question.conditional_on) return true;
    const { question_id, value } = question.conditional_on;
    const answer = responses[question_id];
    if (Array.isArray(value)) return value.includes(answer);
    return answer === value;
  };

  const renderQuestion = (q) => {
    if (!isVisible(q)) return null;
    const val = responses[q.id] || '';

    const inputClass = "w-full px-4 py-2.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent";

    return (
      <div key={q.id} className="space-y-2">
        <label className="block text-sm font-medium text-virgo-teal-300">
          {q.question_text}
          {q.is_required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {q.help_text && <p className="text-xs text-virgo-teal-500">{q.help_text}</p>}

        {q.question_type === 'text' && (
          <input type="text" value={val} onChange={e => updateResponse(q.id, e.target.value)} className={inputClass} placeholder="Enter your answer" />
        )}

        {q.question_type === 'textarea' && (
          <textarea value={val} onChange={e => updateResponse(q.id, e.target.value)} className={inputClass} rows={3} placeholder="Enter your answer" />
        )}

        {q.question_type === 'number' && (
          <input type="number" value={val} onChange={e => updateResponse(q.id, e.target.value)} className={inputClass} placeholder="0" />
        )}

        {q.question_type === 'currency' && (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-500">$</span>
            <input type="number" step="0.01" value={val} onChange={e => updateResponse(q.id, e.target.value)}
              className={`${inputClass} pl-7`} placeholder="0.00" />
          </div>
        )}

        {q.question_type === 'date' && (
          <input type="date" value={val} onChange={e => updateResponse(q.id, e.target.value)}
            className={inputClass} />
        )}

        {q.question_type === 'select' && (
          <select value={val} onChange={e => updateResponse(q.id, e.target.value)} className={inputClass}>
            <option value="">Select an option</option>
            {(q.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        )}

        {q.question_type === 'multiselect' && (
          <div className="space-y-2">
            {(q.options || []).map((opt, i) => {
              const selected = Array.isArray(val) ? val : [];
              return (
                <label key={i} className="flex items-center gap-2 text-virgo-teal-300 text-sm cursor-pointer">
                  <input type="checkbox" checked={selected.includes(opt)}
                    onChange={e => {
                      const newVal = e.target.checked ? [...selected, opt] : selected.filter(v => v !== opt);
                      updateResponse(q.id, newVal);
                    }}
                    className="rounded bg-virgo-navy-600 border-virgo-teal-700 text-cyan-500 focus:ring-cyan-500" />
                  {opt}
                </label>
              );
            })}
          </div>
        )}

        {q.question_type === 'yes_no' && (
          <div className="flex gap-3">
            {['Yes', 'No'].map(opt => (
              <button key={opt} onClick={() => updateResponse(q.id, opt)}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  val === opt
                    ? 'bg-cyan-500 text-white'
                    : 'bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
                }`}>
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // List view
  if (!assignmentId && !questionnaire) {
    const statusBadge = (status) => {
      const colors = {
        assigned: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        in_progress: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        completed: 'bg-green-500/20 text-green-300 border-green-500/30',
      };
      return `px-2 py-0.5 rounded-full text-xs border ${colors[status] || colors.assigned}`;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
        <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
            <button onClick={() => navigate('/portal/dashboard')} className="text-virgo-teal-400 hover:text-white"><ArrowLeft size={24} /></button>
            <ClipboardList className="text-cyan-400" size={24} />
            <h1 className="text-xl font-bold text-white">Questionnaires</h1>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 py-8">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" /></div>
          ) : questionnaires.length === 0 ? (
            <div className="text-center py-16 text-virgo-teal-500">
              <ClipboardList className="mx-auto mb-3 opacity-50" size={48} />
              <p>No questionnaires assigned</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questionnaires.map(q => (
                <button key={q.assignment_id} onClick={() => loadQuestionnaire(q.assignment_id)}
                  className="w-full bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5 text-left hover:border-cyan-500/50 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-semibold">{q.title}</h3>
                      {q.description && <p className="text-virgo-teal-500 text-sm mt-1">{q.description}</p>}
                      {q.case_number && <p className="text-virgo-teal-500 text-xs mt-1">Case: {q.case_number}</p>}
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <span className={statusBadge(q.status)}>{q.status.replace('_', ' ')}</span>
                      {q.due_date && <p className="text-virgo-teal-500 text-xs mt-1">Due: {new Date(q.due_date).toLocaleDateString()}</p>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // Questionnaire form view
  if (loading || !questionnaire) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        {error ? <p className="text-red-400">{error}</p> :
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />}
      </div>
    );
  }

  // Group questions by section
  const sections = {};
  const noSection = [];
  (questionnaire.questions || []).forEach(q => {
    if (q.section) {
      if (!sections[q.section]) sections[q.section] = [];
      sections[q.section].push(q);
    } else {
      noSection.push(q);
    }
  });

  const isCompleted = questionnaire.status === 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => { setQuestionnaire(null); if (!assignmentId) loadList(); else navigate('/portal/questionnaires'); }}
              className="text-virgo-teal-400 hover:text-white"><ArrowLeft size={24} /></button>
            <h1 className="text-lg font-bold text-white">{questionnaire.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-virgo-teal-500 text-xs flex items-center gap-1"><Save size={12} /> Saving...</span>}
            {lastSaved && !saving && (
              <span className="text-virgo-teal-500 text-xs">Saved {lastSaved.toLocaleTimeString()}</span>
            )}
            {isCompleted && <span className="px-3 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded-full text-xs">Completed</span>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {questionnaire.description && (
          <p className="text-virgo-teal-400 mb-6">{questionnaire.description}</p>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-6 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-400" />
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}

        {/* Ungrouped questions */}
        {noSection.length > 0 && (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6 space-y-5">
            {noSection.map(q => renderQuestion(q))}
          </div>
        )}

        {/* Grouped by section */}
        {Object.entries(sections).map(([section, questions]) => (
          <div key={section} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-virgo-teal-700/30">{section}</h2>
            <div className="space-y-5">
              {questions.map(q => renderQuestion(q))}
            </div>
          </div>
        ))}

        {/* Submit */}
        {!isCompleted && (
          <div className="flex justify-between items-center">
            <button onClick={() => autoSave(responses)} disabled={saving}
              className="px-6 py-3 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 flex items-center gap-2 transition-all">
              <Save size={18} /> {saving ? 'Saving...' : 'Save Progress'}
            </button>
            <button onClick={handleSubmit} disabled={submitting}
              className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 flex items-center gap-2 transition-all shadow-lg shadow-cyan-500/30">
              <Send size={18} /> {submitting ? 'Submitting...' : 'Submit Questionnaire'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalQuestionnaires;
